// The preview HOST's data load — public only (PREVIEW-HOST-PUBLIC-ONLY,
// Nika 2026-10-09: "Sandboxed iframe in the app + public-only host").
//
// `<owner>--<key>.preview.symbols.app` renders what the SERVER serves to an
// ANONYMOUS request, and nothing else. One question per env — the anonymous
// public data route (`fetchPublic(envKey)`, the SDK's
// `getPublicProjectDataByKey`) — down the env chain:
//
//   data                       → render it (`_resolvedEnv` names the env)
//   null on every env          → `project_unavailable`
//   { code: password_required} → `project_password_protected` (password card —
//                                the unlock is anonymous too)
//   any other throw            → rethrown untouched (transport / 5xx: retryable)
//
// `null` is the SDK's answer for a private project (403 not_public), a missing
// one (404 project_not_found) and a public one with no published env (404
// env_not_found) alike. They become ONE error with ONE set of fields on
// purpose: the preview host must not tell a stranger which keys exist. There is
// deliberately no visibility probe and no authed fallback here — the host never
// holds a session, so neither could add anything but an oracle.
export async function loadPublicPreviewData({ owner, key, env, fetchPublic } = {}) {
  const envOrder = env
    ? [...new Set([env, 'production', 'staging', 'development'])]
    : ['production', 'staging', 'development']
  const requestedEnv = env || 'production'
  let passwordEnv = null
  for (const tryEnv of envOrder) {
    let pub = null
    try {
      pub = await fetchPublic(tryEnv)
    } catch (e) {
      // The project IS published in this env; the visitor lacks the password.
      if (e?.code === 'password_required') {
        passwordEnv = tryEnv
        break
      }
      throw e
    }
    if (pub) {
      return {
        ...pub,
        _resolvedEnv: tryEnv,
        _requestedEnv: requestedEnv,
        _usedFallback: tryEnv !== requestedEnv
      }
    }
  }
  const projectKey = `${owner}/${key}`
  const err = new Error(
    passwordEnv
      ? `Project "${projectKey}" is password-protected`
      : `Project "${projectKey}" is not available`
  )
  err.code = passwordEnv ? 'project_password_protected' : 'project_unavailable'
  err.owner = owner
  err.key = key
  err.triedEnvs = envOrder
  err.passwordEnv = passwordEnv
  throw err
}
